import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import {
  faArrowLeft,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../Components/Page/Page.css";
import "./UserManagement.css";
import * as XLSX from "xlsx";

interface User {
  id: string;
  email: string;
  firstname: string;
  middlename?: string;
  lastname: string;
  streetaddress?: string;
  streetaddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  companyName?: string;
  companyType?: string;
  title?: string;
  profession: string[];
  professionOther?: string;
  interests: string[];
  interestsOther?: string;
  createdat: string;
  updatedat: string;
  last_login: string;
  mndaStatus: string;
  entityid: number;
  activeind: boolean;
  roles?: string[];
}

interface PasswordUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  email: string;
  onPasswordUpdate: (
    userId: string,
    newPassword: string,
    email: string,
  ) => Promise<void>;
}

interface ImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  firstname: string;
  lastname: string;
}

export default function UserManagement() {
  const { setUser: setUserContext, setImpersonating } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user, hasRole } = useUser();
  const navigate = useNavigate();
  const {
    getUsers,
    updateUserPassword,
    deleteUser,
    getUserById,
    getRoles,
    getUserPermissions,
  } = useAPI();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedeMail, setSelectedeMail] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState(() => {
    return localStorage.getItem("userFilter") || "";
  });

  const [isImpersonationModalOpen, setIsImpersonationModalOpen] =
    useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<{
    firstname: string;
    lastname: string;
  } | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User | null;
    direction: "ascending" | "descending" | null;
  }>({
    key: null,
    direction: null,
  });
  const [hideInactiveUsers, setHideInactiveUsers] = useState<boolean>(false);

  const fetchUsers = useCallback(
    async (selName: string) => {
      try {
        setIsLoading(true);
        setError("");
        const response = await getUsers(selName);
        if (response?.data) {
          setUsers(response.data);
        } else {
          throw new Error("Failed to fetch users");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    },
    [getUsers],
  );

  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate("/login");
      return;
    }

    // Check if user has administrator role
    if (!hasRole("administrator")) {
      navigate("/");
      return;
    }

    fetchUsers(selectedName);
  }, [user, navigate, fetchUsers, hasRole, selectedName]);

  useEffect(() => {
    localStorage.setItem("userFilter", selectedName);
  }, [selectedName]);

  const handleClear = () => {
    setSelectedName(""); // clears input
    localStorage.setItem("userFilter", ""); // ensures blank is saved
  };

  const handleEdit = (userId: string) => {
    navigate(`/user-management/edit/${userId}`);
  };

  const handleAdd = () => {
    navigate("/user-management/add");
  };

  const handleContractStatus = () => {
    navigate("/user-management/ContractStatus");
  };

  const handleUserActivity = () => {
    navigate("/user-management/UserActivity");
  };

  const handlePasswordUpdate = async (
    userId: string,
    newPassword: string,
    email: string,
  ) => {
    try {
      const addedbyID = user?.id ?? "admin";

      await updateUserPassword(
        userId,
        newPassword,
        addedbyID,
        email,
        "User Management",
      );
    } catch (error) {
      console.error("Error updating password:", error);
      throw error;
    }
  };

  const handlePasswordClick = (userId: string, email: string) => {
    setSelectedUserId(userId);
    setSelectedeMail(email);
    setIsPasswordModalOpen(true);
  };

  const handleImpersonate = async (userId: string, email: string) => {
    try {
      const userResponse = await getUserById(userId);
      const data = userResponse.data;

      // Set the impersonated user data for the modal
      setImpersonatedUser({
        firstname: data.firstname,
        lastname: data.lastname,
      });

      // get user permissions
      const userPermissionsResponse = await getUserPermissions(userId);
      const userPermissions = userPermissionsResponse || [];
      // Get all available roles to match with user's roles
      const allRolesResponse = await getRoles();
      const allRoles = allRolesResponse || [];

      // Convert comma-separated roles string to array of Role objects with correct IDs
      const rolesArray = data.roles
        ? data.roles.split(",").map((roleName: string) => {
            const trimmedRoleName = roleName.trim();
            // Find the matching role in allRoles array
            const matchedRole = allRoles.find(
              (role: any) => role.name === trimmedRoleName,
            );
            return {
              id: matchedRole ? matchedRole.id : 0, // Use actual ID or fallback to 0
              name: trimmedRoleName,
            };
          })
        : [];

      const userWithData = {
        ...data,
        permissions: userPermissions || [],
        roles: rolesArray || [],
      };

      setUserContext(userWithData);

      // Set impersonation state to true
      setImpersonating(true);

      // Show the impersonation modal
      setIsImpersonationModalOpen(true);

      // Navigate to profile after a short delay to allow modal to show
      setTimeout(() => {
        navigate("/profile");
      }, 2000);
    } catch (error) {
      console.error("Error during impersonation:", error);
      setError("Failed to impersonate user");
    }
  };

  const handleDelete = async (
    userFN: string,
    userLN: string,
    userId: string,
    email: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete the user "${userFN} ${userLN}"? This action cannot be undone.`,
      )
    ) {
      try {
        const addedbyID = user?.id ?? "admin";
        await deleteUser(userId, addedbyID, email);
        // Refresh the users list after successful deletion
        fetchUsers(selectedName);
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to delete user",
        );
      }
    }
  };

  const handleSort = (key: keyof User) => {
    let direction: "ascending" | "descending" | null = "ascending";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  const getSortedUsers = () => {
    let filteredUsers = users;

    // Filter out inactive users if toggle is enabled
    if (hideInactiveUsers) {
      filteredUsers = users.filter((user) => user.activeind === true);
    }

    if (!sortConfig.key || !sortConfig.direction) {
      return filteredUsers;
    }

    return [...filteredUsers].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      // Handle different data types
      let aStr: string;
      let bStr: string;

      if (Array.isArray(aValue)) {
        aStr = aValue.join(", ");
        bStr = Array.isArray(bValue) ? bValue.join(", ") : "";
      } else if (typeof aValue === "boolean") {
        aStr = aValue ? "Yes" : "No";
        bStr = typeof bValue === "boolean" ? (bValue ? "Yes" : "No") : "";
      } else if (typeof aValue === "number") {
        aStr = aValue.toString();
        bStr = typeof bValue === "number" ? bValue.toString() : "";
      } else {
        aStr = aValue ? aValue.toString().toLowerCase() : "";
        bStr = bValue ? bValue.toString().toLowerCase() : "";
      }

      if (aStr < bStr) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey: keyof User) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} />;
    }
    return sortConfig.direction === "ascending" ? (
      <FontAwesomeIcon icon={faSortUp} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} />
    );
  };

  const calculateStatistics = () => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    // Use filtered users for statistics if hiding inactive users
    const usersForStats = hideInactiveUsers
      ? users.filter((user) => user.activeind === true)
      : users;

    const totalUsers = usersForStats.length;
    const activeUsers = usersForStats.filter(
      (user) => user.activeind === true,
    ).length;
    const usersThisWeek = usersForStats.filter(
      (user) => new Date(user.createdat) >= oneWeekAgo,
    ).length;
    const usersThisMonth = usersForStats.filter(
      (user) => new Date(user.createdat) >= oneMonthAgo,
    ).length;
    const usersThisYear = usersForStats.filter(
      (user) => new Date(user.createdat) >= startOfYear,
    ).length;

    return {
      totalUsers,
      activeUsers,
      usersThisWeek,
      usersThisMonth,
      usersThisYear,
    };
  };

  const handleExportToSpreadsheet = () => {
    // Prepare data for export
    const exportData = users.map((user) => ({
      "First Name": user.firstname,
      "Middle Name": user.middlename || "",
      "Last Name": user.lastname,
      Email: user.email,
      Address: user.streetaddress || "",
      Address2: user.streetaddress2 || "",
      City: user.city || "",
      State: user.state || "",
      Zip: user.zip || "",
      Country: user.country || "",
      Phone: user.phone || "",
      "Company Name": user.companyName || "",
      "Company Type": user.companyType || "",
      Title: user.title || "",
      Profession: Array.isArray(user.profession)
        ? user.profession.join(", ")
        : "",
      "Profession Other": user.professionOther || "",
      Interests: Array.isArray(user.interests) ? user.interests.join(", ") : "",
      "Interests Other": user.interestsOther || "",
      "Date Registered": new Date(user.createdat).toLocaleString(),
      "Last Updated": new Date(user.updatedat).toLocaleString(),
      "Last Login": user.last_login
        ? new Date(user.last_login).toLocaleString()
        : "",
      "MNDA Status": user.mndaStatus,
      "Entity ID": user.entityid || "",
      Active: user.activeind ? "Yes" : "No",
      Roles: user.roles
        ? typeof user.roles === "string"
          ? user.roles
          : Array.isArray(user.roles)
            ? user.roles.map((role: any) => role.name || role).join(", ")
            : ""
        : "",
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Users");

    // Generate filename with current date
    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `IceGems_Users_export_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="generalPage">
      <Header2 />
      <div className="usermanagement-container">
        <div className="pageContainer">
          <div className="button-container">
            <button className="back-button" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
          </div>

          <div className="usermanagement-header">
            <div className="usermanagement-actions">
              <h1 className="usermanagement-title">User Management</h1>
              <div className="header-controls">
                <label className="toggle-control">
                  <input
                    type="checkbox"
                    checked={hideInactiveUsers}
                    onChange={(e) => setHideInactiveUsers(e.target.checked)}
                  />
                  <span className="toggle-label">Hide Inactive Users</span>
                </label>
                <button
                  onClick={handleExportToSpreadsheet}
                  className="export-button"
                  disabled={isLoading || users.length === 0}
                >
                  Export to Spreadsheet
                </button>
              </div>
            </div>
          </div>

          {!isLoading && users.length > 0 && (
            <div className="statistics-container">
              <div className="statistics-grid">
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().totalUsers}
                  </div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().activeUsers}
                  </div>
                  <div className="stat-label">Active Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().usersThisWeek}
                  </div>
                  <div className="stat-label">Added Last 7 Days</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().usersThisMonth}
                  </div>
                  <div className="stat-label">Added Last 30 Days</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {calculateStatistics().usersThisYear}
                  </div>
                  <div className="stat-label">Added This Year</div>
                </div>
              </div>
            </div>
          )}

          <div className="usermanagement-actions">
            <button onClick={handleAdd} className="add-user-button">
              Add New User
            </button>

            <button
              onClick={handleContractStatus}
              className="contract-status-button"
            >
              Contract Status
            </button>

            <button
              onClick={handleUserActivity}
              className="contract-status-button"
            >
              User Activity
            </button>
          </div>
          <div style={{ marginLeft: 0, marginRight: "auto" }}>
            <label className="marginright marginleft">
              Search Name / Email
            </label>
            <input
              type="text"
              name="nameFilter"
              value={selectedName}
              onChange={(e) => setSelectedName(e.target.value)}
              placeholder="Search..."
            />
            {selectedName && (
              <button
                type="button"
                onClick={handleClear}
                className="clear-button"
              >
                Clear
              </button>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead className="table-header">
                  <tr>
                    <th
                      onClick={() => handleSort("lastname")}
                      className="sortable-header"
                    >
                      Name {getSortIcon("lastname")}
                    </th>
                    <th
                      onClick={() => handleSort("email")}
                      className="sortable-header"
                    >
                      Email {getSortIcon("email")}
                    </th>
                    <th
                      onClick={() => handleSort("createdat")}
                      className="sortable-header"
                    >
                      Date Registered {getSortIcon("createdat")}
                    </th>
                    <th
                      onClick={() => handleSort("last_login")}
                      className="sortable-header"
                    >
                      Last Login {getSortIcon("last_login")}
                    </th>
                    <th
                      onClick={() => handleSort("mndaStatus")}
                      className="sortable-header"
                    >
                      Contract Status {getSortIcon("mndaStatus")}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedUsers().map((user) => (
                    <tr key={user.id} className="table-row">
                      <td>
                        {user.lastname}, {user.firstname}
                      </td>
                      <td>{user.email}</td>
                      <td>{new Date(user.createdat).toLocaleDateString()}</td>
                      <td>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleString()
                          : "Never"}
                      </td>
                      <td>
                        <div>
                          <table className="contractstatus-table">
                            <thead className="contractstatus-header"></thead>
                            <tbody>
                              {user.mndaStatus === null ? (
                                <tr className="contractstatus-row">
                                  <td className="contractstatus-row">None</td>
                                </tr>
                              ) : (
                                Object.entries(user.mndaStatus).map(
                                  ([type, status]) => (
                                    <tr
                                      className="contractstatus-row"
                                      key={type}
                                    >
                                      <td className="contractstatus-row">
                                        {status}
                                      </td>
                                    </tr>
                                  ),
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                      <td>
                        {user.activeind ? (
                          <>
                            <button
                              onClick={() => handleEdit(user.id)}
                              className="action-button edit-button"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                handlePasswordClick(user.id, user.email)
                              }
                              className="action-button password-button"
                            >
                              Update Password
                            </button>
                            <button
                              onClick={() =>
                                handleImpersonate(user.id, user.email)
                              }
                              className="action-button impersonate-button"
                            >
                              Impersonate
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(
                                  user.firstname,
                                  user.lastname,
                                  user.id,
                                  user.email,
                                )
                              }
                              className="action-button delete-button"
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="not-active-text">Not Active</span>
                            <button
                              onClick={() =>
                                handleDelete(
                                  user.firstname,
                                  user.lastname,
                                  user.id,
                                  user.email,
                                )
                              }
                              className="action-button delete-button"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <PasswordUpdateModal
            isOpen={isPasswordModalOpen}
            onClose={() => {
              setIsPasswordModalOpen(false);
              setSelectedUserId(null);
              setSelectedeMail(null);
            }}
            userId={selectedUserId || ""}
            email={selectedeMail || ""}
            onPasswordUpdate={handlePasswordUpdate}
          />

          <ImpersonationModal
            isOpen={isImpersonationModalOpen}
            onClose={() => {
              setIsImpersonationModalOpen(false);
              setImpersonatedUser(null);
            }}
            firstname={impersonatedUser?.firstname || ""}
            lastname={impersonatedUser?.lastname || ""}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
}

const PasswordUpdateModal: React.FC<PasswordUpdateModalProps> = ({
  isOpen,
  onClose,
  userId,
  email,
  onPasswordUpdate,
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    try {
      await onPasswordUpdate(userId, newPassword, email);
      setSuccess("Password updated successfully");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update password",
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2 className="modal-title">Update Password</h2>
        {error && <div className="error-text">{error}</div>}
        {success && <div className="success-text">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="submit-button">
              Update Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ImpersonationModal: React.FC<ImpersonationModalProps> = ({
  isOpen,
  onClose,
  firstname,
  lastname,
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content impersonation-modal">
        <h2 className="modal-title">Impersonation Active</h2>
        <div className="impersonation-message">
          <p>
            Impersonating user{" "}
            <strong>
              {firstname} {lastname}
            </strong>
            .
          </p>
          <p>To return to your ID, logout and log in again.</p>
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="submit-button">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
